import { reactive, ref } from 'vue'

// Shared reactive state for the on-page debug bar (DebugBar.vue). perfLogger.js
// pushes into this as requests/long tasks/paint events happen; DebugBar.vue
// just renders whatever is in here, live.

const MAX_ENTRIES = 300
const SESSION_KEY = 'debugBarEnabled'

// Hard block on the real production domain - never show request/response
// data there, regardless of query params or a stale localStorage value.
const PRODUCTION_HOSTNAME = 'partner.haandvaerker.dk'

function isProductionHost () {
    return typeof window !== 'undefined' && window.location.hostname === PRODUCTION_HOSTNAME
}

function setSessionEnabled (on) {
    if (typeof window === 'undefined') return
    try {
        window.sessionStorage.setItem(SESSION_KEY, on ? '1' : '0')
    } catch {
        // Ignore storage errors (private mode, denied access, etc.)
    }
}

function getSessionEnabled () {
    if (typeof window === 'undefined') return null
    try {
        return window.sessionStorage.getItem(SESSION_KEY)
    } catch {
        return null
    }
}

function clearSessionEnabled () {
    if (typeof window === 'undefined') return
    try {
        window.sessionStorage.removeItem(SESSION_KEY)
    } catch {
        // Ignore storage errors (private mode, denied access, etc.)
    }
}

function isReloadNavigation () {
    if (typeof window === 'undefined' || typeof performance === 'undefined') return false

    const nav = performance.getEntriesByType('navigation')?.[0]
    return nav?.type === 'reload'
}

function resolveEnabled () {
    if (isProductionHost()) return false

    const params = new URLSearchParams(window.location.search)
    if (params.has('debugbar')) {
        const enabled = params.get('debugbar') !== '0'
        setSessionEnabled(enabled)
        return enabled
    }

    // Let a full browser refresh bring the debug bar back.
    if (isReloadNavigation()) {
        clearSessionEnabled()
        return true
    }

    const stored = getSessionEnabled()
    if (stored === '1') return true
    if (stored === '0') return false

    // Default on everywhere except production.
    return true
}

export const debugBarEnabled = ref(typeof window !== 'undefined' ? resolveEnabled() : false)

export function setDebugBarEnabled (on) {
    if (on && isProductionHost()) return
    debugBarEnabled.value = on
    setSessionEnabled(on)
}

// Epoch ms of when this page navigation actually started (not just when this
// module happened to load), so "time since start" lines up with the real page load.
export const pageStartTime = typeof performance !== 'undefined' && performance.timeOrigin
    ? performance.timeOrigin
    : Date.now()

export const debugEntries = reactive([])

let seq = 0

export function addDebugEntry (entry) {
    const id = ++seq
    debugEntries.push({ id, at: Date.now(), ...entry })
    if (debugEntries.length > MAX_ENTRIES) {
        debugEntries.splice(0, debugEntries.length - MAX_ENTRIES)
    }
    return id
}

export function updateDebugEntry (id, patch) {
    const entry = debugEntries.find(e => e.id === id)
    if (entry) Object.assign(entry, patch)
}

// For Pinia stores (or anything else) to record that a piece of data was
// served from cache instead of firing a network request - shows up in the
// debug bar's request table alongside real API calls, with source: 'pinia'
// instead of 'network', duration ~0 since nothing was awaited.
export function logCacheHit (label) {
    addDebugEntry({
        type: 'api',
        method: 'CACHE',
        url: label,
        status: 'cached',
        durationMs: 0,
        source: 'pinia'
    })
}

export function clearDebugEntries () {
    debugEntries.splice(0, debugEntries.length)
}
